import moment from 'moment';
import { useSelector } from 'react-redux';

import {
    platform,
    CppEvents,
    CppCoachActions,
    CppUserRoles,
} from '../Analytics/constants';
import { deriveUserSubscriptionType } from '../Analytics/subscriptionType';
import { trackEvent } from '../Analytics/TrackedClick';

// Pro College is the entitled club, so it wins over Free College: a coach on
// both used to get whichever `clubMemberships` happened to list first, which
// is one of the ways `cpp_coach_school_id` came back wrong (ENG-2735).
const findCollegeCoachMembership = (user) => {
    const memberships = user?.clubMemberships ?? [];
    const tierOf = (m) => m?.tierTypeName?.toLowerCase();
    return memberships.find((m) => tierOf(m) === 'pro college') ??
        memberships.find((m) => tierOf(m) === 'free college') ??
        null;
};

// These always concern a specific prospect, so `player_id` is emitted even when
// it resolves to null: a null in Segment is a visible bug, a missing column is
// an invisible one.
const PROSPECT_LEVEL_COACH_ACTIONS = [
    CppCoachActions.CPP_COACH_PROSPECT_LIKED,
    CppCoachActions.CPP_COACH_PROSPECT_ALERT_TOGGLED,
    CppCoachActions.CPP_COACH_CONTACT_INITIATED,
];

const useCPPTracking = () => {
    const user = useSelector(({ auth }) => auth?.user);
    const graduationDate = useSelector(
        (state) => state.profile?.data?.studentInfo?.graduationDate,
    );

    const userSubscriptionStatus = deriveUserSubscriptionType(user);
    const collegeCoachMembership = findCollegeCoachMembership(user);
    const userRole = collegeCoachMembership ? CppUserRoles.CPP_COACH : CppUserRoles.CPP_PLAYER;
    const cppCoachSchoolId = collegeCoachMembership?.clubId ?? null;
    const gradMoment = graduationDate ? moment(graduationDate) : null;
    const gradYear = gradMoment?.isValid() ? gradMoment.year() : null;

    const basePayload = {
        platform,
        userSubscriptionStatus,
        userRole,
    };

    const trackPlayerEngagement = (playerAction, extraProps = {}) => {
        trackEvent(CppEvents.CPP_PLAYER_ENGAGEMENT, {
            ...basePayload,
            ...(gradYear != null && { gradYear }),
            player_action: playerAction,
            ...extraProps,
        });
    };

    const trackCoachEngagement = (coachAction, surface, extraProps = {}) => {
        const {
            playerId,
            player_id: playerIdSnakeCase,
            cpp_coach_school_id: schoolIdFallback,
            ...rest
        } = extraProps;

        const resolvedPlayerId = playerIdSnakeCase ?? playerId ?? null;
        const isProspectLevel = PROSPECT_LEVEL_COACH_ACTIONS.includes(coachAction);

        // The old conditional spread dropped this key entirely when derivation
        // came back null — the "sometimes arrives as null" symptom in ENG-2735.
        // The fallback only fills that hole; a derived value still wins.
        const schoolId = cppCoachSchoolId ?? schoolIdFallback ?? null;

        trackEvent(CppEvents.CPP_COACH_ENGAGEMENT, {
            ...basePayload,
            // DS-96 requires gradYear on EVERY cpp_coach_engagement.
            gradYear: gradYear ?? null,
            cpp_coach_school_id: schoolId,
            coach_action: coachAction,
            surface,
            ...(isProspectLevel || resolvedPlayerId != null
                ? { player_id: resolvedPlayerId }
                : {}),
            ...rest,
        });
    };

    return { trackPlayerEngagement, trackCoachEngagement };
};

export default useCPPTracking;
